import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  StarIcon,
  GiftIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { ListPageSkeleton } from '../ui/PageSkeleton';
import EmptyState from '../ui/EmptyState';
import PageHeader from '../ui/PageHeader';
import ProspectLevelStars from '../ui/ProspectLevelStars';

function formatDate(isoString, locale) {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

function AdminVendorReviewsView({ companyFilter = null, requestCompanyId = null }) {
  const { t, language } = useLanguage();
  const r = t.vendorReviews;
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const config = { withCredentials: true };
      if (requestCompanyId) {
        config.params = { company_id: requestCompanyId };
      }
      const response = await axios.get('/api/admin/vendor-reviews', config);
      let items = response.data.reviews || [];
      // Skip client filter when API already scoped — avoid empty lists when
      // rows omit company_id (same class as Prospects platform-scope bug).
      if (
        companyFilter
        && !requestCompanyId
        && items.some((item) => item.company_id != null)
      ) {
        items = items.filter(
          (item) => item.company_id != null
            && String(item.company_id) === String(companyFilter),
        );
      }
      setReviews(items);
    } catch (error) {
      console.error('Error loading vendor reviews:', error);
      toast.error(r.failedToLoad);
    } finally {
      setLoading(false);
    }
  }, [companyFilter, r.failedToLoad, requestCompanyId]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  if (loading) {
    return <ListPageSkeleton rows={5} />;
  }

  return (
    <div className="space-y-6 xl:space-y-7">
      <PageHeader
        title={r.title}
        subtitle={r.subtitle}
        icon={StarIcon}
      />

      {reviews.length === 0 ? (
        <EmptyState
          icon={ChatBubbleLeftRightIcon}
          title={r.emptyTitle}
          description={r.emptyDesc}
        />
      ) : (
        <div className="space-y-4">
          {reviews.map((item, index) => (
            <motion.article
              key={item.claim_id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.04 }}
              className="card-premium p-5"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-3">
                    <div className="icon-tile h-11 w-11 shrink-0 from-amber-500 to-orange-500">
                      <GiftIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-secondary-900 ">
                        {item.reward_name}
                      </h3>
                      {item.company_name ? (
                        <p className="mt-1 text-xs font-medium text-orange-600 ">{item.company_name}</p>
                      ) : null}
                      <div className="mt-2 grid gap-2 text-sm text-secondary-600  sm:grid-cols-2">
                        <p className="inline-flex items-center gap-1.5">
                          <UserIcon className="h-4 w-4 shrink-0" />
                          <span>
                            <span className="font-medium text-secondary-700 ">
                              {r.deliveredBy}:
                            </span>{' '}
                            {item.vendor_username || '—'}
                          </span>
                        </p>
                        <p className="inline-flex items-center gap-1.5">
                          <UserIcon className="h-4 w-4 shrink-0" />
                          <span>
                            <span className="font-medium text-secondary-700 ">
                              {r.receivedBy}:
                            </span>{' '}
                            {item.client_username || '—'}
                          </span>
                        </p>
                      </div>
                      <p className="mt-2 text-xs text-secondary-500 ">
                        {r.confirmedOn}{' '}
                        {formatDate(item.client_confirmed_at, language === 'es' ? 'es' : 'en')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="shrink-0 space-y-3 rounded-2xl border border-secondary-200/80 bg-secondary-50/50 p-4   lg:min-w-[220px]">
                  <div>
                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-secondary-500">
                      {r.productRating}
                    </p>
                    <ProspectLevelStars
                      value={item.review?.product_rating || 0}
                      readOnly
                      size="sm"
                      ariaLabel={r.productRating}
                    />
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-secondary-500">
                      {r.vendorRating}
                    </p>
                    <ProspectLevelStars
                      value={item.review?.vendor_rating || 0}
                      readOnly
                      size="sm"
                      ariaLabel={r.vendorRating}
                    />
                  </div>
                </div>
              </div>

              {item.review?.review_text ? (
                <blockquote className="mt-4 rounded-xl border border-secondary-200/70 bg-white/60 px-4 py-3 text-sm italic text-secondary-700   ">
                  “{item.review.review_text}”
                </blockquote>
              ) : (
                <p className="mt-4 text-sm text-secondary-500 ">{r.noComment}</p>
              )}
            </motion.article>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminVendorReviewsView;
